import { ShieldAlert, Gauge, AlertTriangle, Wrench, FileWarning, Calendar } from "lucide-react";

const LEVEL_TONE = {
    low: { bg: "bg-bl-teal-tint", text: "text-bl-primary", label: "Low" },
    moderate: { bg: "bg-amber-50", text: "text-amber-700", label: "Moderate" },
    high: { bg: "bg-rose-50", text: "text-rose-700", label: "High" },
    weak: { bg: "bg-amber-50", text: "text-amber-700", label: "Weak" },
    strong: { bg: "bg-bl-teal-tint", text: "text-bl-primary", label: "Strong" },
};

function Cell({ icon: Icon, label, value, tone }) {
    return (
        <div className="bl-card p-5 flex gap-3">
            <Icon className="w-4 h-4 text-bl-primary mt-1 flex-shrink-0" />
            <div className="min-w-0">
                <div className="text-[10px] tracking-[0.2em] uppercase text-bl-muted">
                    {label}
                </div>
                <div className={"mt-1 font-display font-semibold text-base " + (tone || "text-bl")}>
                    {value}
                </div>
            </div>
        </div>
    );
}

/**
 * Premium Deal Risk Snapshot — executive-style briefing card.
 *
 * Renders only when stored `closing_strategy.deal_risk_snapshot` exists.
 * No AI calls — pure render from JSON.
 */
export default function DealRiskSnapshot({ snapshot }) {
    if (!snapshot || typeof snapshot !== "object") return null;
    const risk = LEVEL_TONE[snapshot.overall_deal_risk] || LEVEL_TONE.moderate;
    const lev = LEVEL_TONE[snapshot.negotiation_leverage] || LEVEL_TONE.moderate;

    return (
        <section
            className="bl-card p-6 md:p-8"
            data-testid="deal-risk-snapshot"
        >
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                    <div className="bl-eyebrow">
                        <ShieldAlert className="w-3.5 h-3.5" /> Deal Risk Snapshot · Premium
                    </div>
                    <h2 className="mt-3 font-display font-semibold text-2xl md:text-3xl tracking-tight text-bl">
                        Your buyer's briefing.
                    </h2>
                    {snapshot.headline && (
                        <p className="mt-3 text-bl-muted leading-relaxed max-w-2xl">
                            {snapshot.headline}
                        </p>
                    )}
                </div>
                <span
                    className={
                        "px-3 py-1.5 rounded-full text-xs font-medium tracking-wider uppercase " +
                        risk.bg +
                        " " +
                        risk.text
                    }
                    data-testid="deal-risk-overall-pill"
                >
                    Overall risk · {risk.label}
                </span>
            </div>

            <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                <Cell
                    icon={Gauge}
                    label="Negotiation leverage"
                    value={lev.label}
                    tone={lev.text}
                />
                <Cell
                    icon={AlertTriangle}
                    label="Immediate follow-up"
                    value={snapshot.immediate_followup_needed ? "Yes" : "No"}
                />
                <Cell
                    icon={Wrench}
                    label="Specialist quotes recommended"
                    value={
                        typeof snapshot.specialist_quotes_recommended === "number"
                            ? snapshot.specialist_quotes_recommended
                            : "—"
                    }
                />
                <Cell
                    icon={FileWarning}
                    label="Financing / insurance flags"
                    value={snapshot.financing_insurance_flags ? "Yes" : "No"}
                />
                <Cell
                    icon={Calendar}
                    label="Contingency-sensitive items"
                    value={
                        typeof snapshot.contingency_sensitive_count === "number"
                            ? snapshot.contingency_sensitive_count
                            : "—"
                    }
                />
            </div>
        </section>
    );
}
